import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    BadgeCheck, GraduationCap, Mail, MapPin, Calendar,
    BookOpen, ScrollText, Globe2, Phone, Clock, AlertTriangle,
    CheckCircle2, XCircle,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { openCertificatePdf } from "@/lib/certificateDownload";
import ExtensionRequestDialog from "@/components/student/ExtensionRequestDialog";
import { RadialProgress } from "@/components/ui/radial-progress";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch { return iso; }
};

/**
 * Student profile — shows the friendly Student ID (HCC-YYYY-NNNN) that
 * appears on certificates, plus enrolment + completion summary. Read-only
 * for now; admins handle name / residency edits from the admin panel.
 */
export default function StudentProfile() {
    const { user, refresh } = useAuth();
    const [enrollments, setEnrollments] = useState(null);
    const [certificates, setCertificates] = useState([]);
    const [requests, setRequests] = useState([]);
    const [extDialog, setExtDialog] = useState(null); // enrollment object

    const loadAll = () => {
        return Promise.all([
            api.get("/me/enrollments").then((r) => r.data).catch(() => []),
            api.get("/me/certificates").then((r) => r.data).catch(() => []),
            api.get("/me/extension-requests").then((r) => r.data).catch(() => []),
        ]).then(([enr, cert, ext]) => {
            setEnrollments(enr);
            setCertificates(cert);
            setRequests(ext);
        });
    };

    useEffect(() => {
        // Refresh user once on mount so a freshly-minted student_id from
        // the backfill task lands without a manual reload.
        refresh?.();
        loadAll();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fast lookup: any pending request keyed by enrollment id
    const pendingByEnr = useMemo(() => {
        const m = {};
        for (const r of requests) {
            if (r.status === "pending") m[r.enrollment_id] = r;
        }
        return m;
    }, [requests]);

    if (!user) return null;
    const studentId = user.student_id;
    const initials = (user.name || "")
        .split(" ").filter(Boolean).slice(0, 2)
        .map((p) => p[0]?.toUpperCase()).join("") || "S";

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6"
             data-testid="student-profile-page">
            <header className="flex items-center gap-4">
                <div
                    className="h-16 w-16 rounded-full bg-gradient-to-br from-primary to-primary/70 text-primary-foreground flex items-center justify-center font-display text-2xl"
                    data-testid="profile-avatar"
                >
                    {initials}
                </div>
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                        My profile
                    </div>
                    <h1 className="font-display text-3xl text-primary leading-tight">
                        {user.name}
                    </h1>
                </div>
            </header>

            <Card className="border-primary/15">
                <CardContent className="p-6 space-y-5">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                            Student details
                        </div>
                        {user.role === "student" ? (
                            <Badge variant="outline" className="gap-1">
                                <GraduationCap className="h-3 w-3" /> Student
                            </Badge>
                        ) : null}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field
                            icon={BadgeCheck}
                            label="Student ID"
                            value={studentId || "Pending — assigned at first enrolment"}
                            mono={!!studentId}
                            highlight={!!studentId}
                            testId="profile-student-id"
                            sub="This number appears on every certificate you earn."
                        />
                        <Field icon={Mail} label="Email" value={user.email} />
                        <Field
                            icon={Globe2}
                            label="Residency"
                            value={
                                user.residency === "international"
                                    ? "International"
                                    : user.residency === "domestic"
                                        ? "Domestic (Canada)"
                                        : "Not set"
                            }
                            sub="Drives which tuition tier appears on program cards."
                        />
                        <Field
                            icon={Calendar}
                            label="Member since"
                            value={fmtDate(user.created_at)}
                        />
                        {user.phone ? (
                            <Field
                                icon={Phone}
                                label="Phone"
                                value={user.phone}
                            />
                        ) : null}
                        {user.country ? (
                            <Field
                                icon={Globe2}
                                label="Country"
                                value={user.country}
                            />
                        ) : null}
                    </div>
                </CardContent>
            </Card>

            <Card className="border-primary/15">
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-primary" />
                        <h2 className="font-display text-lg text-primary">
                            Enrolments
                        </h2>
                    </div>
                    {enrollments === null ? (
                        <div className="space-y-2">
                            <Skeleton className="h-10 w-full" />
                            <Skeleton className="h-10 w-full" />
                        </div>
                    ) : enrollments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            You haven't enrolled in any program yet.
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {enrollments.map((e) => {
                                const win = e.access_window || {};
                                const expired = win.state === "expired";
                                const notStarted = win.state === "not_started";
                                const pending = pendingByEnr[e.id];
                                return (
                                <div
                                    key={e.id}
                                    className={`rounded-md border px-3 py-2.5 ${
                                        expired
                                            ? "border-amber-400/60 bg-amber-50/60"
                                            : notStarted
                                                ? "border-sky-400/40 bg-sky-50/60"
                                                : "border-border bg-muted/20"
                                    }`}
                                    data-testid={`enrollment-row-${e.id}`}
                                >
                                    <div className="flex flex-col sm:flex-row items-start gap-4">
                                        <div className="shrink-0 self-center sm:self-start">
                                            <RadialProgress
                                                size={92}
                                                compact
                                                lessonsCompleted={e.lessons_completed || 0}
                                                totalLessons={e.total_lessons || 0}
                                                chaptersCompleted={e.chapters_completed || 0}
                                                totalChapters={e.total_chapters || 0}
                                                flagged={e.flagged_attempts || 0}
                                                testId={`enrollment-ring-${e.id}`}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0 w-full">
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-primary truncate">
                                                {e.program_title || e.program_id}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                Enrolled {fmtDate(e.enrolled_at)}
                                                {e.completed_at
                                                    ? ` · Completed ${fmtDate(e.completed_at)}`
                                                    : ""}
                                                {e.residency
                                                    ? ` · ${e.residency}`
                                                    : ""}
                                            </div>
                                            {(e.start_date || e.end_date) ? (
                                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                                    Access:{" "}
                                                    {e.start_date
                                                        ? fmtDate(e.start_date)
                                                        : "open"}
                                                    {" → "}
                                                    {e.end_date
                                                        ? fmtDate(e.end_date)
                                                        : "open"}
                                                </div>
                                            ) : null}
                                        </div>
                                        <Badge
                                            variant={
                                                expired
                                                    ? "outline"
                                                    : e.completed_at
                                                        ? "default"
                                                        : "outline"
                                            }
                                            className={`capitalize ${
                                                expired
                                                    ? "border-amber-500 text-amber-700 bg-amber-100/60"
                                                    : notStarted
                                                        ? "border-sky-500 text-sky-700 bg-sky-100/60"
                                                        : ""
                                            }`}
                                        >
                                            {expired
                                                ? "Access expired"
                                                : notStarted
                                                    ? "Starts soon"
                                                    : e.completed_at
                                                        ? "Completed"
                                                        : "In progress"}
                                        </Badge>
                                    </div>

                                    {(expired || notStarted) && (
                                        <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                                            <p className="text-xs text-foreground/80 flex items-center gap-1.5">
                                                <Clock className="h-3.5 w-3.5" />
                                                {expired
                                                    ? `Your window closed on ${fmtDate(win.end_date)}.`
                                                    : `Your access starts on ${fmtDate(win.start_date)}.`}
                                            </p>
                                            {pending ? (
                                                <Badge
                                                    variant="outline"
                                                    className="border-amber-500 text-amber-700 bg-amber-100/60 gap-1"
                                                    data-testid={`enrollment-pending-${e.id}`}
                                                >
                                                    <Clock className="h-3 w-3" />
                                                    Request pending · {pending.requested_days}d
                                                </Badge>
                                            ) : expired ? (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="border-primary/50 text-primary hover:bg-primary/10 gap-1.5"
                                                    onClick={() => setExtDialog(e)}
                                                    data-testid={`enrollment-request-extension-${e.id}`}
                                                >
                                                    <Clock className="h-3.5 w-3.5" />
                                                    Request more time
                                                </Button>
                                            ) : null}
                                        </div>
                                    )}
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card className="border-primary/15">
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <ScrollText className="h-4 w-4 text-primary" />
                        <h2 className="font-display text-lg text-primary">
                            Certificates
                        </h2>
                    </div>
                    {certificates.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            Finish a program to earn your first certificate.
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {certificates.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => openCertificatePdf(c.id)}
                                    className="w-full text-left flex items-center justify-between rounded-md border border-sky-300/60 bg-sky-50/70 px-3 py-2.5 hover:bg-sky-50 transition-colors"
                                    data-testid={`certificate-row-${c.id}`}
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-primary truncate">
                                            {c.program_title}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Issued {fmtDate(c.issued_at)}
                                        </div>
                                    </div>
                                    <Badge variant="outline" className="border-sky-400 text-sky-700">
                                        View PDF
                                    </Badge>
                                </button>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Decisions / history of grace requests, only when there's
                anything other than pending to show (pending shows on rows). */}
            {requests.some((r) => r.status !== "pending") && (
                <Card className="border-border" data-testid="extension-history-card">
                    <CardContent className="p-6 space-y-3">
                        <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-primary" />
                            <h2 className="font-display text-lg text-primary">
                                Access requests
                            </h2>
                        </div>
                        <div className="space-y-2">
                            {requests
                                .filter((r) => r.status !== "pending")
                                .slice(0, 5)
                                .map((r) => (
                                    <div
                                        key={r.id}
                                        className="flex items-start justify-between flex-wrap gap-2 rounded-md border border-border bg-muted/20 px-3 py-2.5"
                                        data-testid={`extension-row-${r.id}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-foreground truncate">
                                                {r.program_title}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                Requested {r.requested_days}d on{" "}
                                                {fmtDate(r.created_at)}
                                                {r.status === "approved" && r.new_end_date
                                                    ? ` · New end: ${fmtDate(r.new_end_date)} (+${r.approved_days}d)`
                                                    : ""}
                                            </div>
                                            {r.decision_note ? (
                                                <div className="text-[11px] text-muted-foreground italic mt-0.5">
                                                    Note: {r.decision_note}
                                                </div>
                                            ) : null}
                                        </div>
                                        {r.status === "approved" ? (
                                            <Badge className="bg-sky-500/15 text-sky-700 border border-sky-500/30 gap-1">
                                                <CheckCircle2 className="h-3 w-3" />
                                                Approved
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-rose-500/15 text-rose-700 border border-rose-500/30 gap-1">
                                                <XCircle className="h-3 w-3" />
                                                Denied
                                            </Badge>
                                        )}
                                    </div>
                                ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            <ExtensionRequestDialog
                open={!!extDialog}
                onOpenChange={(o) => !o && setExtDialog(null)}
                enrollment={extDialog}
                onSubmitted={() => loadAll()}
            />
        </div>
    );
}

function Field({ icon: Icon, label, value, sub, mono, highlight, testId }) {
    return (
        <div data-testid={testId}>
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-1">
                <Icon className="h-3 w-3" />
                <MapPin className="hidden" />
                {label}
            </div>
            <div
                className={`text-sm ${
                    highlight
                        ? "text-primary font-display text-lg tracking-wide"
                        : "text-foreground"
                } ${mono ? "font-mono" : ""}`}
            >
                {value}
            </div>
            {sub ? (
                <p className="text-[11px] text-muted-foreground mt-1">
                    {sub}
                </p>
            ) : null}
        </div>
    );
}
